// `extension-alert-candidates-preview-master-profile/*` — the three cards that make up most of
// a candidate profile: work experience, education and skills.
//
// ⚠️ ALMOST EVERY FIELD IS SERVER-AUTHORED HTML, bound with `ng-bind-html`. `jobDescription`
// arrives as markup — `<span class='profile-preview-main-comp-title'>Main competencies:</span>`
// followed by `<span class='non-matched-skill'>CRM</span>` chips — and the chips ARE that
// markup; there is no skills array behind them. So these render through
// `dangerouslySetInnerHTML`, which is the faithful port and needs saying plainly: the safety
// argument is that it is the tenant's own API rendering into the tenant's own frame, exactly as
// upstream does. Escaping it instead shows a wall of angle brackets.
//
// `jobTitle`, `jobEmployer`, `jobPeriod`, `jobEmployerLocation`, every education field and every
// skill name and period are all bound the same way, because the server marks up matched terms.
import type { ReactElement } from 'react'
import { TextReadMore } from '../common/TextReadMore.tsx'
import { timeAgo } from '../common/timeAgo.ts'
import { shouldShowLastUpdatedIcon, validationDateWithFallback, type EnrichableEntity } from './enrichment.ts'

/** `IProfilePreviewDataDTO` — the response of `getCandidateProfilePreviewAndMatchingSummary`. */
export interface ProfilePreviewExperience {
  jobTitle?: string | null
  jobEmployer?: string | null
  jobDescription?: string | null
  jobPeriod?: string | null
  jobEmployerLocation?: string | null
  logoUrl?: string | null
  addedByWoo?: boolean
}

export interface ProfilePreviewEducation {
  degreeType?: string | null
  fieldOfStudy?: string | null
  institute?: string | null
  year?: string | number | null
}

export interface ProfilePreviewSkill {
  name?: string | null
  period?: string | null
  current?: boolean
}

export interface ProfilePreviewData {
  experience?: ProfilePreviewExperience[] | null
  education?: ProfilePreviewEducation[] | null
  skills?: ProfilePreviewSkill[] | null
}

/** The `ng-bind-html` of this port. See the note at the top before reaching for an escape. */
function html(markup: string | null | undefined): { __html: string } {
  return { __html: markup ?? '' }
}

export interface ProfileExperienceSectionProps {
  experience: readonly ProfilePreviewExperience[]
  /** `EntityType.CLIENT_CONTACT` retitles the whole card. */
  entity?: 'candidates' | 'clientContacts'
  /**
   * `entityAlertInfo` — OPTIONAL upstream (`'<?'`), because the external-source snippet renders
   * this same card with no alert entity behind it. Absent means no validation line.
   */
  entityAlertInfo?: EnrichableEntity
  /** `window.careero.relevantDaysForEnriched`; see `shouldShowLastUpdatedIcon`. */
  relevantDaysForEnriched?: number
}

export function ProfileExperienceSection({
  experience,
  entity = 'candidates',
  entityAlertInfo,
  relevantDaysForEnriched,
}: ProfileExperienceSectionProps): ReactElement {
  const showLastUpdated = shouldShowLastUpdatedIcon(entityAlertInfo, relevantDaysForEnriched)
  const recordsAdded = entityAlertInfo?.enrichedRecordsAdded ?? 0

  return (
    <div className="preview-profile-data-experience">
      <div className="snippet-section">
        <div className="snippet-section-header preview-profile-experience-header">
          <div>
            {/* ⚠️ THE HEADING IS THE ENTITY'S, NOT THE SCREEN'S: a client contact's card reads
                "Past companies" over the same data and the same markup. */}
            <strong>{entity === 'clientContacts' ? 'Past companies' : 'Work experience'}</strong>
          </div>
          {!showLastUpdated ? null : (
            <div className="preview-profile-experience-header-data">
              <div>
                <i className="fa fa-check" aria-hidden="true" />
              </div>
              <div className="mL10">
                <div className="experience-updated-validated-text">
                  {/* ⚠️ "Updated" AND "Validated" ARE THE SAME LINE, and the word turns on
                      `enrichedExtraExperience` — whether Woo ADDED anything, not whether it
                      looked. A candidate Woo confirmed and changed nothing about reads
                      "Validated by Woo"; one it added a job to reads "Updated by Woo". */}
                  {entityAlertInfo?.enrichedExtraExperience === true ? 'Updated' : 'Validated'} by Woo{' '}
                  {timeAgo(validationDateWithFallback(entityAlertInfo ?? {}))}
                </div>
                {recordsAdded === 0 ? null : (
                  <div className="experience-added-records-text">
                    Added {String(recordsAdded)} record{recordsAdded === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="snippet-section-body preview-profile-experience-body">
          {experience.map((exp, index) => (
            // eslint-disable-next-line react/no-array-index-key -- `ng-repeat` is positional too.
            <div className="preview-profile-experience-item" key={index}>
              <div className="preview-profile-experience-item-logo">
                <div className="experience-item-logo-container">
                  {/* A logo OR the fallback glyph — never both, and never an empty `img`. */}
                  {exp.logoUrl === undefined || exp.logoUrl === null || exp.logoUrl === '' ? (
                    <i className="fa fa-building" />
                  ) : (
                    <img src={exp.logoUrl} alt="" />
                  )}
                </div>
              </div>
              <div className="preview-profile-experience-item-content">
                <div className="experience-item-job-title-container">
                  <span dangerouslySetInnerHTML={html(exp.jobTitle)} />
                  {exp.addedByWoo === true ? <span className="title-added-by-woo">Added by Woo</span> : null}
                </div>
                <div className="experience-item-employer-container">
                  {/* ⚠️ "N/A" IS A RENDERED FALLBACK, not an empty row — the employer line is
                      always present. */}
                  {exp.jobEmployer === undefined || exp.jobEmployer === null || exp.jobEmployer === '' ? (
                    <span>N/A</span>
                  ) : (
                    <span dangerouslySetInnerHTML={html(exp.jobEmployer)} />
                  )}
                </div>
                {exp.jobPeriod === undefined || exp.jobPeriod === null || exp.jobPeriod === '' ? null : (
                  <div className="experience-item-period-container">
                    <span dangerouslySetInnerHTML={html(exp.jobPeriod)} />
                  </div>
                )}
                {exp.jobEmployerLocation === undefined ||
                exp.jobEmployerLocation === null ||
                exp.jobEmployerLocation === '' ? null : (
                  <div className="experience-item-location-container">
                    <span dangerouslySetInnerHTML={html(exp.jobEmployerLocation)} />
                  </div>
                )}
                {exp.jobDescription === undefined || exp.jobDescription === null || exp.jobDescription === '' ? null : (
                  // `dir="auto"` is upstream's — descriptions come back in mixed scripts, and
                  // an RTL one has to lay itself out without the frame being told.
                  <div className="experience-item-description-container" dir="auto">
                    <TextReadMore text={exp.jobDescription} lineLimit={6} displayAsHtml />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * `getInstituteGraduationYearText` — joined with ", " only when BOTH halves exist.
 *
 * ⚠️ UPSTREAM TESTS `!!edu.year`, SO A YEAR OF 0 IS ABSENT. That is not hypothetical tidying:
 * the field is a number, a parser that failed on a date writes 0 rather than omitting it, and
 * `String(0)` would put a bare "0" under an institute name.
 */
export function instituteGraduationYear(edu: ProfilePreviewEducation): string {
  const institute = edu.institute ?? ''
  const year = edu.year === undefined || edu.year === null || edu.year === 0 || edu.year === '' ? '' : String(edu.year)
  const delimiter = institute !== '' && year !== '' ? ', ' : ''
  return `${institute}${delimiter}${year}`
}

export function ProfileEducationSection({
  education,
}: {
  education: readonly ProfilePreviewEducation[]
}): ReactElement {
  return (
    <div className="preview-profile-data-education">
      <div className="snippet-section">
        <div className="snippet-section-header preview-profile-education-header">
          <div>
            <strong>Education</strong>
          </div>
        </div>
        <div className="snippet-section-body preview-profile-education-body">
          {education.map((edu, index) => (
            // eslint-disable-next-line react/no-array-index-key -- positional upstream too.
            <div className="preview-profile-education-item" key={index}>
              {edu.degreeType === undefined || edu.degreeType === null || edu.degreeType === '' ? null : (
                <div className="education-item-degree-container" dangerouslySetInnerHTML={html(edu.degreeType)} />
              )}
              {edu.fieldOfStudy === undefined || edu.fieldOfStudy === null || edu.fieldOfStudy === '' ? null : (
                <div
                  className="education-item-field-of-study-container"
                  dangerouslySetInnerHTML={html(edu.fieldOfStudy)}
                />
              )}
              {instituteGraduationYear(edu) === '' ? null : (
                <div
                  className="education-item-institute-year-container"
                  dangerouslySetInnerHTML={html(instituteGraduationYear(edu))}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * `PreviewMasterProfileSkillsController.$onInit` — CURRENT SKILLS FIRST, everything else in the
 * order the server sent it.
 *
 * ⚠️ NOTHING IN THE TEMPLATE SAYS THIS HAPPENS. The card renders `ctrl.previewSkills`, which
 * `$onInit` builds by sorting an array of INDICES and mapping back — upstream's way of getting
 * a stable sort out of `Array.prototype.sort`, which was not stable in the engines this code
 * was written for. `sort` is stable in every engine that runs this port, so comparing on
 * `current` alone would give the same answer; the index tiebreak is kept because it is what
 * makes the intent readable, and because it costs nothing.
 */
export function sortSkillsCurrentFirst(
  skills: readonly ProfilePreviewSkill[],
): readonly ProfilePreviewSkill[] {
  return skills
    .map((skill, index) => ({ skill, index }))
    .sort((a, b) => {
      if (a.skill.current !== b.skill.current) return a.skill.current === true ? -1 : 1
      return a.index - b.index
    })
    .map((entry) => entry.skill)
}

export function ProfileSkillsSection({ skills: unsorted }: { skills: readonly ProfilePreviewSkill[] }): ReactElement {
  const skills = sortSkillsCurrentFirst(unsorted)

  return (
    <div className="preview-profile-data-skills">
      <div className="snippet-section">
        <div className="snippet-section-header preview-profile-skills-header">
          <div>
            {/* "Skill Set" — capital S on Set, unlike the criteria builder's "Skill set". */}
            <strong>Skill Set</strong>
          </div>
        </div>
        <div className="snippet-section-body preview-profile-skills-body">
          <div className="preview-profile-skills-table">
            {skills.map((skill, index) => (
              // eslint-disable-next-line react/no-array-index-key -- positional upstream too.
              <div className="preview-profile-skill-item-row" key={index}>
                <div className="preview-skill-name" dangerouslySetInnerHTML={html(skill.name)} />
                <div className="preview-skill-current">
                  {skill.current === true ? <div className="preview-skill-current-indication">Current</div> : null}
                </div>
                <div className="preview-skill-period text-right" dangerouslySetInnerHTML={html(skill.period)} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
